import { Input } from '@/components/form/input';
import type { Player, SaleItem } from '@repo/shared-types';
import { Search, User, ShoppingBag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { normalizeString } from '@repo/ui/lib/normalize-string';
import { phoneMatchesSearch } from '@repo/ui/lib/normalize-phone-search';

interface PlayerWithPending {
  playerId?: string;
  name: string;
  lastName: string;
  phone: string;
  pendingCount: number;
}

function getInitials(name: string, lastName: string): string {
  const first = name.charAt(0).toUpperCase();
  const last = lastName.charAt(0).toUpperCase();
  if (first && last) return `${first}${last}`;
  if (first) return first;
  return '?';
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-pink-500',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface CashierPlayerGridProps {
  players: Player[];
  saleItems: SaleItem[];
  onSelectPlayer: (playerId: string) => void;
}

export function CashierPlayerGrid({ players, saleItems, onSelectPlayer }: CashierPlayerGridProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Build list of ALL players with their pending sale counts
  const allPlayers = useMemo(() => {
    // Count pending items per player
    const pendingByPlayerId = new Map<string, number>();
    const pendingByName = new Map<string, number>();

    saleItems.forEach((item) => {
      if (item.player?.id) {
        pendingByPlayerId.set(item.player.id, (pendingByPlayerId.get(item.player.id) || 0) + 1);
      } else if (item.name) {
        pendingByName.set(item.name, (pendingByName.get(item.name) || 0) + 1);
      }
    });

    const result: PlayerWithPending[] = [];

    // Add all registered players
    players.forEach((player) => {
      result.push({
        playerId: player.id,
        name: player.name,
        lastName: player.lastName,
        phone: player.phone,
        pendingCount: pendingByPlayerId.get(player.id) || 0,
      });
    });

    // Add anonymous players (from sale items with name but no player)
    pendingByName.forEach((count, name) => {
      result.push({
        name,
        lastName: '',
        phone: '',
        pendingCount: count,
      });
    });

    // Sort: players with pending items first, then alphabetically
    result.sort((a, b) => {
      if (a.pendingCount > 0 && b.pendingCount === 0) return -1;
      if (a.pendingCount === 0 && b.pendingCount > 0) return 1;
      return `${a.name} ${a.lastName}`.localeCompare(`${b.name} ${b.lastName}`);
    });

    return result;
  }, [players, saleItems]);

  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return allPlayers;
    const normalizedQuery = normalizeString(searchQuery);
    return allPlayers.filter((p) => {
      // Check text fields (name, lastName) with text normalization
      const matchesText =
        normalizeString(p.name).includes(normalizedQuery) ||
        normalizeString(p.lastName).includes(normalizedQuery);

      // Check phone with phone normalization (generic, works for any country)
      const matchesPhone = phoneMatchesSearch(p.phone, searchQuery);

      return matchesText || matchesPhone;
    });
  }, [allPlayers, searchQuery]);

  return (
    <div className="flex flex-col gap-4 h-full p-4">
      {/* Search */}
      <div className="shrink-0">
        <Input
          placeholder="Buscar jugador por nombre, apellido o teléfono..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          preffix={<Search className="size-5 text-neutral-400" />}
          className="text-lg!"
        />
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto min-h-0 pt-2">
        {filteredPlayers.length === 0 ? (
          searchQuery.trim() ? (
            <div className="flex flex-col items-center justify-center h-full text-neutral-400 text-center py-12 gap-4">
              <div className="flex flex-col items-center gap-2">
                <span className="text-xl font-medium text-neutral-500">
                  No se encontraron jugadores
                </span>
                <span className="text-sm text-neutral-400">
                  Puedes continuar sin registrar usando este nombre
                </span>
              </div>
              {(() => {
                const name = searchQuery.trim();
                const initials = getInitials(name, '');
                const colorClass = getAvatarColor(name);
                return (
                  <button
                    type="button"
                    onClick={() => onSelectPlayer(name)}
                    className="
                      relative flex flex-col items-center gap-2 rounded-xl border-2 bg-white p-4
                      shadow-sm transition-all duration-150 cursor-pointer
                      hover:shadow-md hover:border-primary hover:-translate-y-0.5
                      active:translate-y-0 active:shadow-sm border-neutral-100
                      max-w-xs w-full
                    "
                  >
                    <div
                      className={`size-14 md:size-16 rounded-full ${colorClass} flex items-center justify-center text-white text-xl md:text-2xl font-bold shadow-inner shrink-0`}
                    >
                      {initials}
                    </div>
                    <div className="flex flex-col items-center gap-0.5 min-w-0 w-full">
                      <span className="text-base md:text-lg font-semibold text-neutral-800 truncate w-full text-center leading-tight">
                        {name}
                      </span>
                      <span className="text-xs text-primary font-medium mt-0.5">
                        Usar este nombre sin registrar
                      </span>
                    </div>
                  </button>
                );
              })()}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-neutral-400 text-center py-12">
              <User className="size-16 mb-4" />
              <span className="text-xl font-medium text-neutral-500">
                No se encontraron jugadores
              </span>
              <span className="text-sm text-neutral-400 mt-1">
                Intenta con otro término de búsqueda
              </span>
            </div>
          )
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredPlayers.map((player) => {
              const key = player.playerId ?? `anon:${player.name}`;
              const id = player.playerId ?? player.name;
              const initials = getInitials(player.name, player.lastName);
              const colorClass = getAvatarColor(player.name + player.lastName);
              const hasPending = player.pendingCount > 0;

              return (
                <button
                  key={key}
                  onClick={() => onSelectPlayer(id)}
                  className={`
                    relative flex flex-col items-center gap-2 rounded-xl border-2 bg-white p-4
                    shadow-sm transition-all duration-150 cursor-pointer
                    hover:shadow-md hover:border-primary hover:-translate-y-0.5
                    active:translate-y-0 active:shadow-sm
                    ${hasPending ? 'border-primary/30' : 'border-neutral-100'}
                  `}
                >
                  {/* Pending badge */}
                  {hasPending && (
                    <div className="absolute -top-2 -right-2 flex items-center gap-1 bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm">
                      <ShoppingBag className="size-3" />
                      {player.pendingCount}
                    </div>
                  )}

                  {/* Avatar */}
                  <div
                    className={`size-14 md:size-16 rounded-full ${colorClass} flex items-center justify-center text-white text-xl md:text-2xl font-bold shadow-inner shrink-0`}
                  >
                    {initials}
                  </div>

                  {/* Name */}
                  <div className="flex flex-col items-center gap-0.5 min-w-0 w-full">
                    <span className="text-base md:text-lg font-semibold text-neutral-800 truncate w-full text-center leading-tight">
                      {player.name}
                    </span>
                    {player.lastName && (
                      <span className="text-sm md:text-base font-medium text-neutral-600 truncate w-full text-center leading-tight">
                        {player.lastName}
                      </span>
                    )}
                    {player.phone && (
                      <span className="text-xs text-neutral-400 truncate w-full text-center mt-0.5">
                        {player.phone}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
